import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useEyeconxStore} from "../../store/eyeconxStore";
import login from "../../services/auth.service";
import {useNavigate} from "react-router-dom";

function Login() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm();
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const setToken = useEyeconxStore((state) => state.setToken);
    const setUser = useEyeconxStore((state) => state.setUser);
    const setIsAuthenticate = useEyeconxStore((state) => state.setIsAuthenticate);

    const token = useEyeconxStore((state) => state.token);
    const isAuthenticated = useEyeconxStore((state) => state.isAuthenticated);
    const user = useEyeconxStore((state) => state.user);
    const navigate = useNavigate();

    // Load remembered credentials on component mount
    useEffect(() => {
        const rememberedCredentials = localStorage.getItem('rememberedCredentials');
        if (rememberedCredentials) {
            const { username, password } = JSON.parse(rememberedCredentials);
            setValue('username', username);
            setValue('password', password); // Set value through React Hook Form
            setRememberMe(true);
        }
    }, [setValue]);

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            const res = await login(data.username, data.password);

            if (res.data.statusCode === 200) {
                const user = {
                    ID: res.data.uId,
                    name: res.data.username,
                    role: res.data.role,
                    clinicId: res.data.clinicId,
                    authorization: "Bearer " + res.data.token,
                };

                setToken(res.data.token);
                setIsAuthenticate(true);
                setUser(user);

                if (rememberMe) {
                    localStorage.setItem('rememberedCredentials', JSON.stringify({
                        username: data.username,
                        password: data.password
                    }));
                } else {
                    localStorage.removeItem('rememberedCredentials');
                }
            }
        } catch (error) {
            if (!error?.response) {
                setMessage("No Server Response");
            } else if (error.response?.status === 400) {
                setMessage("Missing Username or Password");
            } else if (error.response?.status === 401 || error.response?.status === 403) {
                setMessage("Incorrect Username or Password");
            } else {
                setMessage("Login Failed. Please try again");
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && token != null) {
            if (user.role === "ADMIN") {
                navigate("/dashboard");
            } else {
                setMessage("This is only for Admin access!");
                setIsAuthenticate(false);
                setUser(null);
                setToken(null);
            }
        }
    }, [isAuthenticated, token, user, navigate, setIsAuthenticate, setUser, setToken, setMessage]);

    return (
        <div className="min-vh-100 d-flex align-items-center bg-gradient-primary">
            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5 col-xl-4">
                        <div className="card shadow-sm border-0 rounded-lg overflow-hidden">
                            {/* Card Header */}
                            <div className="card-header bg-white py-3 border-0 text-center">
                                <img
                                    src="https://eyeconx.s3.us-east-2.amazonaws.com/3conx/assets/3f6f63b82cf94f13985e104afd45f226.png"
                                    alt="EyeconX Logo"
                                    className="img-fluid mb-2"
                                    style={{maxWidth: '150px'}}
                                />
                                <h4 className="fw-bold mb-1">Welcome Back</h4>
                                <p className="text-muted small mb-0">Sign in to your admin account</p>
                            </div>

                            {/* Card Body */}
                            <div className="card-body p-3 p-md-4">
                                {message && (
                                    <div className="alert alert-danger d-flex align-items-center mb-3 py-2">
                                        <i className="bi bi-exclamation-circle-fill me-2 small"></i>
                                        <div className="small">{message}</div>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit(onSubmit)}>
                                    {/* Username Field */}
                                    <div className="mb-3">
                                        <label htmlFor="username" className="form-label small fw-medium text-dark">
                                            <i className="bi bi-person me-2 small"></i>Username
                                        </label>
                                        <input
                                            type="text"
                                            id="username"
                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                            {...register("username", { required: true })}
                                            placeholder="Enter your username"
                                            tabIndex="1"
                                        />
                                        {errors.username && (
                                            <div className="invalid-feedback d-flex align-items-center small">
                                                <i className="bi bi-exclamation-circle me-2 small"></i>
                                                Username is required
                                            </div>
                                        )}
                                    </div>

                                    {/* Password Field - Fixed to work with React Hook Form */}
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label small fw-medium text-dark">
                                            <i className="bi bi-key me-2 small"></i>Password
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="password"
                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                {...register("password", { required: true })}
                                                placeholder="Enter your password"
                                                tabIndex="2"
                                            />
                                            <button
                                                className="btn btn-outline-secondary py-1 px-2"
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                            >
                                                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} small`}></i>
                                            </button>
                                            {errors.password && (
                                                <div className="invalid-feedback d-flex align-items-center small">
                                                    <i className="bi bi-exclamation-circle me-2 small"></i>
                                                    Password is required
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Remember Me & Forgot Password */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="rememberMe"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                            />
                                            <label className="form-check-label text-muted small" htmlFor="rememberMe">
                                                Remember me
                                            </label>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="d-grid mb-2">
                                        <button
                                            type="submit"
                                            className="btn btn-primary py-2"
                                            disabled={isLoading}
                                            tabIndex="3"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    <span className="small">Signing in...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-box-arrow-in-right me-2 small"></i>
                                                    <span className="small">Sign In</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Card Footer */}
                            <div className="card-footer bg-light text-center py-2 border-0">
                                <p className="mb-0 text-muted small">Don't have an account? <a href="mailto:support@eyeconx.net" className="text-decoration-none small">Contact support</a></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
